import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, CheckCircle2, UploadCloud, Truck, Building2, ShieldCheck } from 'lucide-react';
import { useSupplierAuth } from '../../lib/supplierAuthStore';
import { SERVICE_DATA } from '../../lib/serviceData';
type RegStep = 'phone' | 'business' | 'services' | 'fleet' | 'done';
export function SupplierRegister() {
  const navigate = useNavigate();
  const {
    signIn
  } = useSupplierAuth();
  const [step, setStep] = useState<RegStep>('phone');
  // Form State
  const [phone, setPhone] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [tradeLicense, setTradeLicense] = useState('');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [truckCount, setTruckCount] = useState('1-5');
  const handleComplete = () => {
    signIn(`+971 ${phone}`, businessName);
    navigate('/supplier/dashboard');
  };
  const steps = ['phone', 'business', 'services', 'fleet', 'done'];
  const currentIdx = steps.indexOf(step);
  return <div className="min-h-screen bg-surface-100 py-12 px-4 sm:px-6 lg:px-8" data-id="element-4043">
      <div className="max-w-3xl mx-auto" data-id="element-4044">
        {/* Header */}
        <div className="flex items-center justify-between mb-8" data-id="element-4045">
          <Link to="/supplier/login" className="flex items-center gap-2.5" data-id="element-4046">
            <img src="https://cdn.magicpatterns.com/uploads/kwTMidwDpg46YyVPyEcdAS/Pair2_(2).svg" alt="Pair2" className="h-7 w-auto" data-id="element-4047" />
            <span className="font-display font-bold text-xl text-gray-900" data-id="element-4048">
              Carrier
            </span>
          </Link>
          {step !== 'done' && <div className="flex items-center gap-2" data-id="element-4049">
              {steps.slice(0, 4).map((s, i) => <div key={s} className="flex items-center" data-id="element-4050">
                  <div className={`w-2.5 h-2.5 rounded-full ${i <= currentIdx ? 'bg-brand-500' : 'bg-gray-200'}`} data-id="element-4051" />
                  {i < 3 && <div className={`w-8 h-0.5 ${i < currentIdx ? 'bg-brand-500' : 'bg-gray-200'}`} data-id="element-4052" />}
                </div>)}
            </div>}
        </div>

        {/* Wizard Card */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-4053">
          <AnimatePresence mode="wait" data-id="element-4054">
            {step === 'phone' && <motion.div key="phone" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="p-8 sm:p-12" data-id="element-4055">
                <div className="w-12 h-12 rounded-2xl bg-gray-900 flex items-center justify-center mb-6" data-id="element-4056">
                  <ShieldCheck className="w-6 h-6 text-white" data-id="element-4057" />
                </div>
                <h2 className="text-3xl font-display font-bold text-gray-900 mb-2" data-id="element-4058">
                  Create your account
                </h2>
                <p className="text-gray-500 mb-8" data-id="element-4059">
                  Let's start with your mobile number.
                </p>

                <div className="flex gap-2 mb-8 max-w-md" data-id="element-4060">
                  <div className="flex items-center gap-2 px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl shrink-0" data-id="element-4061">
                    <span className="text-base" data-id="element-4062">🇦🇪</span>
                    <span className="font-bold text-gray-900" data-id="element-4063">+971</span>
                  </div>
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="50 123 4567" className="flex-1 px-4 py-3.5 rounded-xl border border-gray-200 outline-none text-lg focus:border-brand-300 font-medium" data-id="element-4064" />
                </div>

                <button onClick={() => setStep('business')} disabled={phone.length < 9} className="py-4 px-8 bg-gray-900 hover:bg-black text-white rounded-xl font-bold text-base transition-colors flex items-center gap-2 disabled:opacity-50" data-id="element-4065">
                  Send OTP <ArrowRight className="w-5 h-5" data-id="element-4066" />
                </button>
                <p className="mt-4 text-xs text-gray-400" data-id="element-4067">
                  Demo mode: OTP step skipped for prototype.
                </p>
              </motion.div>}

            {step === 'business' && <motion.div key="business" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="p-8 sm:p-12" data-id="element-4068">
                <button onClick={() => setStep('phone')} className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-900 mb-6" data-id="element-4069">
                  <ArrowLeft className="w-4 h-4" data-id="element-4070" /> Back
                </button>
                <div className="w-12 h-12 rounded-2xl bg-gray-900 flex items-center justify-center mb-6" data-id="element-4071">
                  <Building2 className="w-6 h-6 text-white" data-id="element-4072" />
                </div>
                <h2 className="text-3xl font-display font-bold text-gray-900 mb-2" data-id="element-4073">
                  Business Details
                </h2>
                <p className="text-gray-500 mb-8" data-id="element-4074">
                  We need this to verify your company and process payouts.
                </p>

                <div className="space-y-5 max-w-xl" data-id="element-4075">
                  <div data-id="element-4076">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 block" data-id="element-4077">
                      Business Name
                    </label>
                    <input type="text" value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="e.g. FastTrack Logistics LLC" className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-brand-300 font-medium" data-id="element-4078" />
                  </div>
                  <div data-id="element-4079">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 block" data-id="element-4080">
                      Trade License Number
                    </label>
                    <input type="text" value={tradeLicense} onChange={e => setTradeLicense(e.target.value)} placeholder="e.g. 1234567" className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-brand-300 font-medium" data-id="element-4081" />
                  </div>
                  <div data-id="element-4082">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 block" data-id="element-4083">
                      Upload Trade License
                    </label>
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-4084">
                      <UploadCloud className="w-8 h-8 text-gray-400 mb-2" data-id="element-4085" />
                      <p className="text-sm font-bold text-gray-700" data-id="element-4086">
                        Click to upload PDF or Image
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-4087">
                        Max file size 5MB
                      </p>
                    </div>
                  </div>
                </div>

                <button onClick={() => setStep('services')} disabled={!businessName || !tradeLicense} className="mt-8 py-4 px-8 bg-gray-900 hover:bg-black text-white rounded-xl font-bold text-base transition-colors flex items-center gap-2 disabled:opacity-50" data-id="element-4088">
                  Continue <ArrowRight className="w-5 h-5" data-id="element-4089" />
                </button>
              </motion.div>}

            {step === 'services' && <motion.div key="services" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="p-8 sm:p-12" data-id="element-4090">
                <button onClick={() => setStep('business')} className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-900 mb-6" data-id="element-4091">
                  <ArrowLeft className="w-4 h-4" data-id="element-4092" /> Back
                </button>
                <h2 className="text-3xl font-display font-bold text-gray-900 mb-2" data-id="element-4093">
                  What services do you offer?
                </h2>
                <p className="text-gray-500 mb-8" data-id="element-4094">
                  Select all categories your fleet can handle.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-4095">
                  {Object.entries(SERVICE_DATA).map(([id, s]) => {
                const isSelected = selectedServices.includes(id);
                return <button key={id} onClick={() => setSelectedServices(prev => isSelected ? prev.filter(x => x !== id) : [...prev, id])} className={`p-4 rounded-2xl border-2 text-left transition-all ${isSelected ? 'border-brand-500 bg-brand-50' : 'border-gray-100 hover:border-gray-200 bg-white'}`} data-id="element-4096">
                        <div className="flex justify-between items-start mb-2" data-id="element-4097">
                          <span className="text-2xl" data-id="element-4098">{s.icon}</span>
                          <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? 'bg-brand-500 border-brand-500' : 'border-gray-300'}`} data-id="element-4099">
                            {isSelected && <CheckCircle2 className="w-3 h-3 text-white" data-id="element-4100" />}
                          </div>
                        </div>
                        <p className="font-bold text-gray-900" data-id="element-4101">{s.name}</p>
                      </button>;
              })}
                </div>

                <button onClick={() => setStep('fleet')} disabled={selectedServices.length === 0} className="py-4 px-8 bg-gray-900 hover:bg-black text-white rounded-xl font-bold text-base transition-colors flex items-center gap-2 disabled:opacity-50" data-id="element-4102">
                  Continue <ArrowRight className="w-5 h-5" data-id="element-4103" />
                </button>
              </motion.div>}

            {step === 'fleet' && <motion.div key="fleet" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="p-8 sm:p-12" data-id="element-4104">
                <button onClick={() => setStep('services')} className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-900 mb-6" data-id="element-4105">
                  <ArrowLeft className="w-4 h-4" data-id="element-4106" /> Back
                </button>
                <div className="w-12 h-12 rounded-2xl bg-gray-900 flex items-center justify-center mb-6" data-id="element-4107">
                  <Truck className="w-6 h-6 text-white" data-id="element-4108" />
                </div>
                <h2 className="text-3xl font-display font-bold text-gray-900 mb-2" data-id="element-4109">
                  Fleet Size
                </h2>
                <p className="text-gray-500 mb-8" data-id="element-4110">
                  Roughly how many trucks do you operate? You can add exact
                  details later.
                </p>

                <div className="flex flex-wrap gap-3 mb-8" data-id="element-4111">
                  {['1-5', '6-15', '16-50', '50+'].map(size => <button key={size} onClick={() => setTruckCount(size)} className={`px-6 py-3 rounded-xl font-bold border-2 transition-all ${truckCount === size ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-4112">
                      {size} Trucks
                    </button>)}
                </div>

                <button onClick={() => setStep('done')} className="py-4 px-8 bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-bold text-base transition-colors flex items-center gap-2 shadow-lg shadow-brand-500/20" data-id="element-4113">
                  Submit Application <CheckCircle2 className="w-5 h-5" data-id="element-4114" />
                </button>
              </motion.div>}

            {step === 'done' && <motion.div key="done" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="p-8 sm:p-16 text-center" data-id="element-4115">
                <div className="w-20 h-20 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-6" data-id="element-4116">
                  <CheckCircle2 className="w-10 h-10" data-id="element-4117" />
                </div>
                <h2 className="text-3xl font-display font-bold text-gray-900 mb-4" data-id="element-4118">
                  Application Received
                </h2>
                <p className="text-gray-600 max-w-md mx-auto mb-8" data-id="element-4119">
                  Thanks for registering {businessName}. Our team will verify
                  your trade license and activate your account within 24 hours.
                </p>

                <div className="p-6 bg-brand-50 rounded-2xl border border-brand-100 max-w-md mx-auto" data-id="element-4120">
                  <p className="text-sm text-brand-800 font-medium mb-4" data-id="element-4121">
                    For this prototype, you can skip the wait and explore the
                    dashboard immediately.
                  </p>
                  <button onClick={handleComplete} className="w-full py-3.5 bg-gray-900 hover:bg-black text-white rounded-xl font-bold transition-colors" data-id="element-4122">
                    Continue to Demo Dashboard
                  </button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}